import axiosInstance from './axiosConfig';
import { Category } from '../../types';

export interface CategoryResponse {
  success: boolean;
  data: Category[];
  message?: string;
}

export interface CategoryCreateRequest {
  name: string;
  description?: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface CategoryUpdateRequest {
  name: string;
  description?: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface CategoryOrderUpdateRequest {
  categories: Array<{
    id: number;
    sort_order: number;
  }>;
}

class CategoryService {
  private baseUrl = '/categories';
  private adminBaseUrl = '/admin/categories';
  private productCategoryBaseUrl = '/product-categories';

  /**
   * 一般ユーザー向け：アクティブなカテゴリ一覧取得
   */
  async getActiveCategories(): Promise<Category[]> {
    try {
      const response = await axiosInstance.get<CategoryResponse>(this.baseUrl);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'カテゴリの取得に失敗しました');
      }
    } catch (error) {
      console.error('Active categories fetch error:', error);
      throw error;
    }
  }

  /**
   * 商品登録向け：商品カテゴリ一覧取得
   */
  async getProductCategories(): Promise<Category[]> {
    try {
      const response = await axiosInstance.get<CategoryResponse>(this.productCategoryBaseUrl);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || '商品カテゴリの取得に失敗しました');
    } catch (error) {
      console.error('Product categories fetch error:', error);
      throw error;
    }
  }

  /**
   * 商品登録向け：商品カテゴリ作成
   */
  async createProductCategory(categoryData: Pick<CategoryCreateRequest, 'name' | 'description'>): Promise<Category> {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        data: Category;
        message?: string;
      }>(this.productCategoryBaseUrl, categoryData);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || '商品カテゴリの作成に失敗しました');
    } catch (error) {
      console.error('Product category creation error:', error);
      throw error;
    }
  }

  /**
   * 管理者用：全カテゴリ一覧取得
   */
  async getAllCategories(): Promise<Category[]> {
    try {
      const response = await axiosInstance.get<CategoryResponse>(this.adminBaseUrl);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'カテゴリの取得に失敗しました');
      }
    } catch (error) {
      console.error('All categories fetch error:', error);
      throw error;
    }
  }

  /**
   * カテゴリ作成
   */
  async createCategory(categoryData: CategoryCreateRequest): Promise<Category> {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        data: Category;
        message?: string;
      }>(this.adminBaseUrl, categoryData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'カテゴリの作成に失敗しました');
      }
    } catch (error) {
      console.error('Category creation error:', error);
      throw error;
    }
  }

  /**
   * カテゴリ更新
   */
  async updateCategory(id: number, categoryData: CategoryUpdateRequest): Promise<Category> {
    try {
      const response = await axiosInstance.put<{
        success: boolean;
        data: Category;
        message?: string;
      }>(`${this.adminBaseUrl}/${id}`, categoryData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'カテゴリの更新に失敗しました');
      }
    } catch (error) {
      console.error('Category update error:', error);
      throw error;
    }
  }

  /**
   * カテゴリ削除
   */
  async deleteCategory(id: number): Promise<void> {
    try {
      const response = await axiosInstance.delete<{
        success: boolean;
        message?: string;
      }>(`${this.adminBaseUrl}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'カテゴリの削除に失敗しました');
      }
    } catch (error) {
      console.error('Category deletion error:', error);
      throw error;
    }
  }

  /**
   * カテゴリの有効/無効切り替え
   */
  async toggleCategory(id: number): Promise<Category> {
    try {
      const response = await axiosInstance.put<{
        success: boolean;
        data: Category;
        message?: string;
      }>(`${this.adminBaseUrl}/${id}/toggle`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'カテゴリの状態変更に失敗しました');
      }
    } catch (error) {
      console.error('Category toggle error:', error);
      throw error;
    }
  }

  /**
   * カテゴリの並び順更新
   */
  async updateCategoryOrder(orderData: CategoryOrderUpdateRequest): Promise<void> {
    try {
      const response = await axiosInstance.put<{
        success: boolean;
        message?: string;
      }>(`${this.adminBaseUrl}/order`, orderData);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'カテゴリの並び順更新に失敗しました');
      }
    } catch (error) {
      console.error('Category order update error:', error);
      throw error;
    }
  }
}

// 名前付きエクスポート用の関数
export const getPublicCategories = () => new CategoryService().getActiveCategories();

export default new CategoryService();
